import {
  Breakpoints,
  DesignTokenSizeEnum,
  Layout,
  Text,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTribalContext } from '@/context/tribal';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import {
  Box,
  CTADownloadBrochure,
  Footer,
  Footnote,
  PageSection,
  PriceBreakdownDropdown,
  SideTabs,
  SideTabsStickyWrapper,
} from '../components';
import PageContainer from '../components/PageContainer/PageContainer';
import Header from '../layouts/single/Header/Header';
import { isMobile } from '../utils/breakpoints';

import { fuelType, normalizeFuelTypeName } from '../constants/fuelType';
import EventBanner from '../features/single/EventBanner/EventBanner';
import GoingElectric from '../layouts/single/GoingElectric/GoingElectric';
import GoingHybrid from '../layouts/single/GoingHybrid/GoingHybrid';
import LikedItems from '../layouts/single/LikedItems/LikedItems';
import ModelPriceAndFeatures from '../layouts/single/ModelPriceAndFeatures/ModelPriceAndFeatures';
import OptionalExtras from '../layouts/single/OptionalExtras/OptionalExtras';
import Specifications from '../layouts/single/Specifications/Specifications';
import {
  CarOptionsEndpoint,
  getActiveConfiguration,
  getBespokeSpecItemsCodes,
  getCarOptionsData,
  getDisabledModelMessage,
  getModelTrim,
  isNotBuildable,
} from '../utils/storeUtils';
import { getP11dPrice } from '../utils/p11d';
import { SectionNames } from '@/analytics/sectionNames';
import {
  performVWBasicErrorMessageLoad,
  performVWBasicPageLoad,
} from '@/analytics/events';
import ServicePlanBanner from '../features/single/ServicePlanBanner/ServicePlanBanner';
import YourWagenBanner from '../features/single/YourWagenBanner/YourWagenBanner';
import YourFinanceCalculation from '../layouts/single/YourFinanceCalculation/YourFinanceCalculation';

const Single = () => {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const { app, setActiveConfiguration } = useGlobalStore();
  const { fleetMode } = useGlobalStore((state) => state.app);
  const { mscId, orderNumberId, vinId } = useParams();
  const [showPriceBreakdownDropdown, setShowPriceBreakdownDropdown] =
    useState(false);
  const headerRef = useRef(null);
  const sectionEls = useRef(new Array());
  const isMobileWidth = useResizeResponsive(isMobile);

  const activeConfiguration: GlobalStoreStateConfiguration | undefined =
    getActiveConfiguration(app);


  const analyticsCommonFields = {
    configuration: activeConfiguration,
    retailers: app.retailers,
    analyticsManager,
    analyticsBuilder,
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    performVWBasicPageLoad(analyticsCommonFields);
  }, []);

  useEffect(() => {
    if (mscId || orderNumberId || vinId) {

      const key = mscId ? 'marketingShortCode' : orderNumberId ? 'orderNumber' : 'vin';

      const param = mscId ? mscId : orderNumberId ? orderNumberId : vinId;

      const activeConfigurationIndex = app.configurations.findIndex(
        (configuration) => configuration[key] === param
      );

      setActiveConfiguration(activeConfigurationIndex);
    }
  }, [mscId, orderNumberId]);

  useEffect(() => {
    const footers = document.getElementsByTagName('footer');
    let footer: HTMLElement;

    const priceDropdownObserver = new IntersectionObserver((entries) => {
      if (entries.every((entry) => !entry.isIntersecting))
        setShowPriceBreakdownDropdown(true);
      else setShowPriceBreakdownDropdown(false);
    });

    if (headerRef.current) priceDropdownObserver.observe(headerRef.current);
    if (!!footers.length) {
      footer = footers[footers.length - 1];
      priceDropdownObserver.observe(footer);
    }

    return () => priceDropdownObserver.disconnect();
  }, [activeConfiguration]);

  useEffect(() => {
    app.configurations.forEach((configuration) => {
      const { pdbData, modelTitle, trimTitle, derivativeTitle } = configuration;
      if (isNotBuildable(pdbData))
        performVWBasicErrorMessageLoad({
          ...analyticsCommonFields,
          error: {
            message: getDisabledModelMessage(
              contentManager,
              modelTitle,
              derivativeTitle || trimTitle
            ),
          },
        });
    });
  }, []);

  if (!activeConfiguration) return null;

  const layoutAppearance = {
    [Breakpoints.default]: [
      {
        name: 'sidebar',
        columns: 24,
      },
      {
        name: 'content',
        columns: 24,
      },
    ],
    [Breakpoints.b960]: [
      {
        name: 'sidebar',
        columns: 4,
      },
      {
        name: 'gap',
        columns: 2,
      },
      {
        name: 'content',
        columns: 18,
      },
    ],
  };
  const getOptionsData = (data: string[], endpoint: CarOptionsEndpoint) => {
    return data?.map((code: string) => {
      const optionData = getCarOptionsData(activeConfiguration, code, endpoint);
      return optionData;
    });
  };
  const extrasCodes = activeConfiguration?.pdbData?.capi?.configuration?.extras;

  const extrasData = getOptionsData(
    extrasCodes || [],
    activeConfiguration.orderNumber || activeConfiguration.vin ? 'stock' : 'config'
  ).filter((item) => item?.visible === true);

  const bespokeItemsCodes = getBespokeSpecItemsCodes(activeConfiguration?.bespokeSpecItems || []);

  const likedItemsCodes = activeConfiguration?.liked?.specItems;
  const standardVideos =
    activeConfiguration?.productData?.technologyItems?.standard ?? [];
  const standardVideosIds = standardVideos.map((video) => video.id);
  const extrasVideos =
    activeConfiguration?.productData?.technologyItems?.extras?.filter(
      ({ id }) => !standardVideosIds.includes(id)
    ) ?? [];
  const technologyVideos = [...extrasVideos, ...standardVideos];

  const fuelTypeName = normalizeFuelTypeName(
    activeConfiguration?.pdbData?.technicalSpecifications?.engine?.fuelTypeName
  );


  const getExtrasNavigationLabel = () => {
    if (
      (extrasData?.length || bespokeItemsCodes.length) &&
      technologyVideos.length
    ) {
      return contentManager.get(
        'extras-section-side-navigation-extras-videos-label'
      );
    } else if (technologyVideos.length) {
      return contentManager.get('extras-section-side-navigation-videos-label');
    }

    return contentManager.get('extras-section-side-navigation-label');
  };

  const priceBreakdownData = {
    prices: [
      {
        label: contentManager.get('price-breakdown-section-base-price-label'),
        price: activeConfiguration?.pdbData?.pricing?.retail,
        ignore: true,
      },
      {
        label: contentManager.get('price-breakdown-section-extras-label'),
        price: activeConfiguration?.pdbData?.pricing?.extras,
        ignore: true,
      },
      {
        label: contentManager.get(
          'price-breakdown-section-bespoke-options-label'
        ),
        info: contentManager.get(
          'price-breakdown-section-set-by-retailer-text'
        ),
        price: activeConfiguration?.pdbData?.pricing?.bespokeExtras,
      },
      {
        label: contentManager.get('price-breakdown-section-subsidy-label'),
        price: activeConfiguration?.pdbData?.pricing?.grants,
      },
      {
        label: contentManager.get('price-breakdown-section-discount-label'),
        price: activeConfiguration?.pdbData?.pricing?.discount,
      },
      {
        label: contentManager.get('price-breakdown-section-allowance-label'),
        info: contentManager.get(
          'price-breakdown-section-set-by-retailer-text'
        ),
        price: activeConfiguration?.pdbData?.pricing?.allowance,
      },
      {
        label: contentManager.get('price-breakdown-section-trade-in-label'),
        info: contentManager.get(
          'price-breakdown-section-set-by-retailer-text'
        ),
        price: activeConfiguration?.pdbData?.pricing?.tradeIn,
      },
      {
        label: contentManager.get('total-price-label'),
        price: activeConfiguration?.pdbData?.pricing?.outstandingTotal,
        bold: true,
        ignore: true,
      },
      ...(activeConfiguration?.pdbData?.fleetMode
        ? [
            {
              label: contentManager.get('p11d-price-label'),
              price: getP11dPrice(activeConfiguration),
              bold: true,
              ignore: true,
            },
          ]
        : []),
    ],
  };

  const pageSections = [
    {
      label: contentManager.get(
        'price-and-features-section-side-navigation-label'
      ),
      id: contentManager.get('price-and-features-section-id'),
      section: (
        <ModelPriceAndFeatures
          activeConfiguration={activeConfiguration}
          priceBreakdownData={priceBreakdownData}
        />
      ),
    },
    {
      label: contentManager.get('specifications-section-side-navigation-label'),
      id: contentManager.get('specifications-section-id'),
      section: fuelTypeName ? (
        <Specifications
          activeConfiguration={activeConfiguration}
          fuelTypeName={fuelTypeName}
        />
      ) : (
        ''
      ),
    },
    ...(extrasCodes?.length ||
    bespokeItemsCodes?.length ||
    technologyVideos.length
      ? [
          {
            label: getExtrasNavigationLabel(),
            id: contentManager.get('extras-section-id'),
            section: (
              <OptionalExtras
                activeConfiguration={activeConfiguration}
                extrasCodes={extrasCodes}
                bespokeItemsCodes={bespokeItemsCodes}
                technologyVideos={technologyVideos}
              />
            ),
          },
        ]
      : []),
    ...(likedItemsCodes?.length
      ? [
          {
            label: contentManager.get(
              'liked-items-section-side-navigation-label'
            ),
            id: contentManager.get('liked-items-section-id'),
            section: (
              <LikedItems
                activeConfiguration={activeConfiguration}
                likedItemsCodes={likedItemsCodes}
              />
            ),
          },
        ]
      : []),
    ...(fuelTypeName === fuelType.ELECTRIC
      ? [
          {
            label: contentManager.get(
              'going-electric-section-side-navigation-label'
            ),
            id: contentManager.get('going-electric-section-id'),
            section: <GoingElectric />,
          },
        ]
      : []),
    ...(fuelTypeName === fuelType.PLUG_IN_HYBRID
      ? [
          {
            label: contentManager.get(
              'going-hybrid-section-side-navigation-label'
            ),
            id: contentManager.get('going-hybrid-section-id'),
            section: <GoingHybrid />,
          },
        ]
      : []),
    ...(activeConfiguration?.finance && activeConfiguration.finance.length > 0 && !fleetMode
      ? [
          {
            label: contentManager.get(
              'your-finance-section-side-navigation-label'
            ),
            id: 'finance-section-id',
            section: (
              <YourFinanceCalculation
                index={1}
                activeConfiguration={activeConfiguration}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div ref={headerRef}>
        <Header appData={app} activeConfiguration={activeConfiguration} />
      </div>

      <PriceBreakdownDropdown
        title={getModelTrim(
          activeConfiguration.modelTitle,
          activeConfiguration.trimTitle
        )}
        subtitle={activeConfiguration.pdbData?.derivative?.title}
        totalPriceLabel={contentManager.get('total-price-label')}
        totalPrice={
          +(activeConfiguration.pdbData?.pricing?.outstandingTotal || 0)
        }
        p11dLabel={contentManager.get('p11d-price-label')}
        p11dPrice={
          activeConfiguration.pdbData?.fleetMode
            ? getP11dPrice(activeConfiguration)
            : null
        }
        priceBreakdown={priceBreakdownData}
        disclaimerText={
          <div>
            <Text color={TokenTextColor.tertiary}>{contentManager.get('prices-disclaimer-text')}</Text>
          </div>
        }
        reveal={showPriceBreakdownDropdown}
      />

      <PageContainer>
        <Box
          display="flex"
          justifyContent="center"
          mb={DesignTokenSizeEnum.static510}
          mt={DesignTokenSizeEnum.static250}
          b960={{
            display: 'block',
            marginTop: DesignTokenSizeEnum.static0,
          }}
        >
          <CTADownloadBrochure sectionName={SectionNames.SINGLE_PAGE_HEADER} />
        </Box>
        <Layout allowOverflowingContent appearance={layoutAppearance}>
          {!isMobileWidth && (
            <SideTabsStickyWrapper>
              <SideTabs
                tabs={pageSections.map(({ id, label }) => ({ id, label }))}
                sections={sectionEls}
                activeConfiguration={activeConfiguration}
              />
            </SideTabsStickyWrapper>
          )}

          <div>
            {pageSections.map(({ section, id }, index) => (
              <PageSection
                ref={(el) => (sectionEls.current[index] = el)}
                id={id}
                key={id}
              >
                {section}
              </PageSection>
            ))}
          </div>
        </Layout>
      </PageContainer>
      <EventBanner />
      <ServicePlanBanner />
      <YourWagenBanner />
      <Footer />
      <Footnote />
    </>
  );
};

export default Single;
